// CreateBinaryMarket — user-facing create-market route (/create-binary) for
// the binary Maniswap Arena (I-075 P2). Replaces the old Mint/Citizen bond
// flow (DECISIONS.md 2026-06-16 (a)). The creator stakes an at-risk OC
// subsidy (min 100), binds a resolution oracle from the I-068 Registry (no
// self-resolution), and becomes the market_maker earning the 1% rake.
//
// Calls the create_user_binary_market RPC (granted to anon/authenticated;
// applied to Arena 2026-06-16). Arena-only — the RPC + binary engine live on
// the Arena Supabase, not Live. Standalone route (rendered outside
// .cockpit-main), self-contained inline styles → avoids the mobile-jailbreak
// CSS sharp edges; matches the SettleBinary / BinaryTradeTicket register.

import React, { useCallback, useEffect, useState } from 'react';
import { supabase, isSupabaseEnabled, getGhostId } from '../lib/supabaseClient';
import { IS_GAME_MODE } from '../lib/mode';

const MIN_SUBSIDY = 100; // mirrors create_user_binary_market's floor (DECISIONS.md 2026-06-16 (d))

interface OracleOption { id: string; display_name: string; oracle_class: number }

export default function CreateBinaryMarket(): React.JSX.Element {
  const [question, setQuestion] = useState('');
  const [openingPct, setOpeningPct] = useState('50');      // P(YES) %, 1–99
  const [subsidyStr, setSubsidyStr] = useState(String(MIN_SUBSIDY));
  const [oracleId, setOracleId] = useState('');
  const [oracles, setOracles] = useState<OracleOption[] | null>(null);
  const [oracleErr, setOracleErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const loadOracles = useCallback(async () => {
    if (!isSupabaseEnabled() || !supabase) {
      setOracleErr('Supabase not configured (this route is Arena-only).');
      setOracles([]);
      return;
    }
    const { data, error: e } = await supabase
      .from('oracle_sources')
      .select('id, display_name, oracle_class')
      .order('oracle_class', { ascending: true });
    if (e) { setOracleErr(e.message); setOracles([]); return; }
    setOracleErr(null);
    setOracles((data ?? []) as OracleOption[]);
  }, []);

  useEffect(() => { loadOracles(); }, [loadOracles]);

  const subsidy = Number(subsidyStr);
  const openingProb = Number(openingPct) / 100;
  const valid =
    question.trim().length > 0 &&
    Number.isFinite(subsidy) && subsidy >= MIN_SUBSIDY &&
    Number.isFinite(openingProb) && openingProb > 0 && openingProb < 1 &&
    oracleId.length > 0;

  async function submit() {
    setError(null); setDone(null);
    if (!isSupabaseEnabled() || !supabase) { setError('Market creation is offline (Supabase not configured).'); return; }
    setSubmitting(true);
    try {
      const marketId = `um_${crypto.randomUUID()}`;
      const { data, error: rpcErr } = await supabase.rpc('create_user_binary_market', {
        p_market_id: marketId,
        p_user_id: getGhostId(),
        p_question: question.trim(),
        p_subsidy: subsidy,
        p_opening_prob: openingProb,
        p_oracle_id: oracleId,
      });
      if (rpcErr) {
        // The RPC raises descriptive messages for: min-subsidy, missing/unknown
        // oracle, and the negative-balance guard (insufficient OC). Surface them.
        setError(/negative|balance/i.test(rpcErr.message)
          ? 'Insufficient OC for this subsidy.'
          : rpcErr.message);
        return;
      }
      const mid = data && typeof data === 'object' ? (data as { market_id?: string }).market_id : marketId;
      setDone(`Market created (${mid}). Your ${subsidy} OC is now at-risk liquidity — you are the market maker and earn 1% of matched volume; the residual returns to you at settlement.`);
      setQuestion(''); setSubsidyStr(String(MIN_SUBSIDY)); setOpeningPct('50'); setConfirming(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const wrap: React.CSSProperties = {
    minHeight: '100vh', background: '#020617', color: '#e5e7eb',
    fontFamily: 'monospace', padding: '32px 20px',
  };
  const field: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'inherit',
    fontFamily: 'monospace', marginBottom: 16,
  };
  const label: React.CSSProperties = { display: 'block', fontSize: 11, opacity: 0.7, marginBottom: 6 };

  if (!IS_GAME_MODE) {
    return (
      <div style={wrap}><div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ color: '#F59E0B', fontSize: 11, letterSpacing: '0.22em', fontWeight: 700 }}>CREATE A MARKET</div>
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 12 }}>
          User-created binary markets are an Arena (play-credits) feature. This build is the Live platform.
        </div>
      </div></div>
    );
  }

  return (
    <div style={wrap}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ color: '#F59E0B', fontSize: 11, letterSpacing: '0.22em', fontWeight: 700, marginBottom: 4 }}>
          CREATE A MARKET · AGORACLE ARENA
        </div>
        <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 24, lineHeight: 1.6 }}>
          You seed the market with an at-risk OC subsidy (min {MIN_SUBSIDY}), set the opening
          probability, and bind a resolution oracle. You become the market maker: you earn 1% of
          matched volume and the residual returns to you at settlement. The operator settles
          against your chosen oracle (no self-resolution).
        </div>

        <label style={label}>Question</label>
        <input style={field} value={question} placeholder="Will …?"
          onChange={(e) => setQuestion(e.target.value)} />

        <label style={label}>Opening probability — P(YES) %</label>
        <input style={field} type="number" min="1" max="99" step="1" value={openingPct}
          onChange={(e) => setOpeningPct(e.target.value)} />

        <label style={label}>Liquidity subsidy (OC, at risk — min {MIN_SUBSIDY})</label>
        <input style={field} type="number" min={MIN_SUBSIDY} step="any" value={subsidyStr}
          onChange={(e) => setSubsidyStr(e.target.value)} />

        <label style={label}>Resolution oracle (required — no self-resolution)</label>
        <select style={field} value={oracleId} onChange={(e) => setOracleId(e.target.value)}>
          <option value="">— select an oracle source —</option>
          {(oracles ?? []).map((o) => (
            <option key={o.id} value={o.id}>{o.display_name} (class {o.oracle_class})</option>
          ))}
        </select>
        {oracleErr && <div style={{ color: '#f88', fontSize: 12, marginBottom: 12 }}>{oracleErr}</div>}
        {oracles && oracles.length === 0 && !oracleErr && (
          <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 12 }}>No oracle sources available.</div>
        )}

        {!confirming ? (
          <button disabled={!valid} onClick={() => { setError(null); setConfirming(true); }}
            style={{
              width: '100%', padding: '11px 0', borderRadius: 8, fontWeight: 700, border: 'none',
              background: '#1f7a4d', color: '#fff', cursor: valid ? 'pointer' : 'default', opacity: valid ? 1 : 0.5,
            }}>
            Create for {Number.isFinite(subsidy) ? subsidy : MIN_SUBSIDY} OC
          </button>
        ) : (
          <div style={{ border: '1px solid #F59E0B', borderRadius: 8, padding: 12, fontSize: 13 }}>
            <div style={{ marginBottom: 10 }}>
              Stake <strong>{subsidy} OC</strong> as at-risk liquidity and open this market at{' '}
              <strong>{openingPct}%</strong>?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={submitting} onClick={submit}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, border: 'none',
                  background: '#F59E0B', color: '#020617', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1,
                }}>
                {submitting ? 'Creating…' : `Confirm — stake ${subsidy} OC`}
              </button>
              <button disabled={submitting} onClick={() => setConfirming(false)}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent', color: 'inherit', cursor: submitting ? 'default' : 'pointer',
                }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && <div style={{ color: '#f88', fontSize: 12, marginTop: 12 }}>{error}</div>}
        {done && <div style={{ color: '#8f8', fontSize: 12, marginTop: 12, lineHeight: 1.6 }}>{done}</div>}
      </div>
    </div>
  );
}
